import React, { useMemo, useState } from "react";
import { NavLink, useSearchParams } from "react-router-dom";

import {
  IResourceComponentsProps,
  useParsed,
  useOne,
  usePermissions,
  useGetIdentity,
  useDelete,
} from "@refinedev/core";
import {
  DateField,
  ExportButton,
  List,
  SaveButton,
  useDataGrid,
} from "@refinedev/mui";

import {
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Box,
  Autocomplete,
  TextField,
  ButtonGroup,
} from "@mui/material";
import {
  AddCircle,
  Delete,
  Edit,
  FilterList,
  RemoveCircle,
  RemoveRedEyeOutlined,
} from "@mui/icons-material";
import {
  GridColumns,
  DataGrid,
  getGridStringOperators,
} from "@mui/x-data-grid";

import { Controller, FieldValues, useForm } from "react-hook-form";
import * as jsurl from "jsurl";

import { useModal } from "components/Modal";

import { theme } from "constants/theme";

import { queryClient } from "App";
import { getFilenamePostfix } from "utils/export";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "rest-data-provider/utils";
import { API_URL } from "utils/env";

import {
  FieldEntityTypeEnum,
  FormEntity,
  ResponseEntityWithoutBody,
  UserEntity,
  UserEntityRoleEnum,
} from "generated";

export type ResponseListType = {
  count: number;
  responses: ResponseEntityWithoutBody[];
};

export const ResponseList: React.FC<IResourceComponentsProps> = () => {
  const parsedData = useParsed();
  const formId = parsedData?.params?.formId;
  const showFilter = parsedData?.params?.showFilter === "true";
  const filter: string = parsedData?.params?.filter || "";

  const submittedBy = parsedData?.params?.filters?.find(
    (filter: any) => filter.field === "submittedBy" && filter.operator === "eq"
  )?.value;

  const [searchParams, setSearchParams] = useSearchParams();

  const meData = useGetIdentity<UserEntity>();
  const isAdmin = usePermissions().data === UserEntityRoleEnum.Admin;

  const { Modal, setOpen } = useModal({ defaultOpen: false });
  const [responseId, setResponseId] = useState("");

  const fetchResourceString = `forms/${formId}/responses${
    !!filter ? "?filter=" + filter : ""
  }`;
  const { dataGridProps } = useDataGrid({
    resource: fetchResourceString,
    queryOptions: {
      enabled: !!meData.data?.id,
    },
  });

  const count = (dataGridProps.rows as unknown as ResponseListType)?.count || 0;

  dataGridProps.rows =
    (dataGridProps.rows as unknown as ResponseListType)?.responses || [];

  const { data: formData, isLoading: formIsLoading } = useOne<FormEntity>({
    resource: `forms`,
    id: formId ?? "",
    queryOptions: {
      enabled: !!dataGridProps?.rows?.length,
    },
  });

  const { mutate: deleteResponseMutate, isLoading: isDeletingResponse } =
    useDelete();

  const {
    filterMode,
    filterModel,
    onFilterModelChange,
    sortingMode,
    sortModel,
    onSortModelChange,
    paginationMode,
    page,
    onPageChange,
    pageSize,
    onPageSizeChange,
    ...restDataGridProps
  } = dataGridProps;

  const exportUrl = `${API_URL}/forms/${formId}/responses/export?limit=${pageSize}&skip=${
    (page || 0) * (pageSize || 20)
  }${submittedBy ? "&submittedBy=" + submittedBy : ""}${
    !!filter ? "&filter=" + filter : ""
  }`;

  const { refetch: refetchExport, isFetching } = useQuery<Blob>(
    ["download"],
    async () => {
      const data = await axiosInstance({
        url: exportUrl,
        method: "GET",
        responseType: "blob", // important
      });

      return data.data;
    },
    {
      enabled: false,
    }
  );

  const exportHandler = async () => {
    try {
      const data = (await refetchExport()).data;
      if (data) {
        const url = window.URL.createObjectURL(data);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          "Response-" + getFilenamePostfix() + ".xlsx"
        ); //or any other extension
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const columns = React.useMemo<GridColumns<ResponseEntityWithoutBody>>(
    () => [
      {
        field: "createdAt",
        flex: 1,
        headerName: "Date",
        minWidth: 150,
        filterable: false,
        renderCell: function render({ value }) {
          return <DateField value={value} />;
        },
      },
      {
        field: "formId",
        flex: 1,
        headerName: "Form",
        minWidth: 250,
        filterable: false,
        sortable: false,

        renderCell() {
          return formIsLoading ? (
            <CircularProgress />
          ) : (
            formData?.data?.label || "-"
          );
        },
      },
      {
        field: "userName",
        flex: 1,
        headerName: "User Name",
        minWidth: 250,
        sortable: true,
      },
      {
        field: "submittedBy",
        flex: 1,
        headerName: "User Id",
        minWidth: 250,
        filterable: isAdmin,
        filterOperators: getGridStringOperators().filter((op) => {
          return op.value === "equals";
        }),
        renderCell({ value }) {
          return <Typography>{value}</Typography>;
        },
      },
      {
        field: "responseNumber",
        flex: 1,
        headerName: "Form Number",
        filterable: false,
        minWidth: 200,
      },
      {
        field: "actions",
        headerName: "Actions",
        filterable: false,
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <IconButton>
                <NavLink
                  to={`/forms/show/${row.formId}?responseId=${row.id}&type=view`}
                >
                  <RemoveRedEyeOutlined color="primary" />
                </NavLink>
              </IconButton>

              <IconButton>
                <NavLink
                  to={`/forms/show/${row.formId}?responseId=${row.id}&type=edit`}
                >
                  <Edit color="primary" />
                </NavLink>
              </IconButton>

              {isAdmin ? (
                <IconButton
                  disabled={isDeletingResponse}
                  onClick={() => {
                    setResponseId(row.id);
                    setOpen(true);
                  }}
                >
                  <Delete color="primary" />
                </IconButton>
              ) : null}
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 120,
      },
    ],
    [formData?.data, formIsLoading, isAdmin, isDeletingResponse, setOpen]
  );

  const deleteResponseHandler = () => {
    deleteResponseMutate(
      {
        resource: `forms/${formId}/responses`,
        id: responseId || "",
        successNotification: () => {
          return {
            description: "Response delete",
            message: `Response successfully deleted.`,
            type: "success",
          };
        },
        errorNotification: (errors) => {
          return {
            description: errors?.message,
            message: errors?.errors?.length
              ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
              : "",
            type: "error",
          };
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            "default",
            fetchResourceString,
            "list",
          ]);
        },
      }
    );
  };

  return (
    <List
      breadcrumb={""}
      title={count ? `Responses (${count})` : undefined}
      contentProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          gap: theme.gap,
        },
      }}
      headerButtons={
        isAdmin && count > 0 ? (
          <>
            <ExportButton
              variant="contained"
              onClick={exportHandler}
              endIcon={
                isFetching ? (
                  <CircularProgress sx={{ color: "white" }} size={20} />
                ) : null
              }
            />
          </>
        ) : null
      }
    >
      {formData?.data ? (
        <>
          <Button
            variant={showFilter ? "contained" : undefined}
            startIcon={<FilterList />}
            sx={{ ml: "auto" }}
            onClick={() => {
              if (searchParams.get("showFilter")) {
                searchParams.delete("showFilter");
                searchParams.delete("filter");
              } else {
                searchParams.set("showFilter", "true");
              }
              setSearchParams(searchParams);
            }}
          >
            {`${showFilter ? "Hide" : "Show"} Filter`}
          </Button>

          {showFilter ? (
            <FilterSection
              formData={formData?.data}
              setFilter={(query) => {
                searchParams.set("filter", jsurl.stringify(query));

                setSearchParams(searchParams);
              }}
            />
          ) : null}
        </>
      ) : null}

      <DataGrid
        columns={columns}
        {...restDataGridProps}
        filterMode={filterMode}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        sortingMode={"client"}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        paginationMode={paginationMode}
        page={page}
        onPageChange={onPageChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        autoHeight
      />

      <Modal
        title="के तपाइँ यो रेकर्ड मेटाउन निश्चित हुनुहुन्छ ?"
        cancelTitle="रद्द गर्नुहोस्"
        okTitle="मेटाउनुहोस्"
        onOk={deleteResponseHandler}
        id="delete"
      />
    </List>
  );
};

type FormFilterType = {
  sectionName: string;
  id: string;
  label: string;
  fieldName: string;
  nestedFieldName?: string;
  type: Omit<FieldEntityTypeEnum, "files fields">;
  acceptabledValues: string[];
};

const stringOperatorOptions = [
  {
    label: "Equals",
    op: "$eq",
  },
];

const numericalOperatorOptions = [
  {
    label: "Equals",
    op: "$eq",
  },
  {
    label: "Greater than or equals",
    op: "$gte",
  },
  {
    label: "Less than or equals",
    op: "$lte",
  },
  {
    label: "Greater & Less than or equals ",
    op: "$gle",
  },
];

const FilterSection: React.FC<{
  formData: FormEntity;
  setFilter: (filter: string) => void;
}> = ({ formData, setFilter }) => {
  const { control, handleSubmit, unregister, watch, setValue } = useForm();

  const [queryArray, setQueryArray] = useState([0]);

  const options: Record<string, FormFilterType> = useMemo(() => {
    return formData.sections.reduce((acc: any, curr) => {
      curr.fields.forEach((field) => {
        if (field.type === FieldEntityTypeEnum.Files) return;

        if (field.type === FieldEntityTypeEnum.Fields) {
          field.fields?.forEach((nestedField) => {
            acc[nestedField.id] = {
              sectionName: curr.name,
              id: nestedField.id,
              label: field.label + " - " + nestedField.label,
              fieldName: field.name,
              nestedFieldName: nestedField.name,
              type: nestedField.type,
              acceptabledValues: nestedField.acceptableValues || undefined,
            };
          });
        } else {
          acc[field.id] = {
            sectionName: curr.name,
            id: field.id,
            label: field.label,
            fieldName: field.name,
            type: field.type,
            acceptabledValues: field.acceptableValues || undefined,
          };
        }
      });

      return acc;
    }, {});
  }, [formData.sections]);

  /**
   *
   * @param values
   * @type {
   * 0: {field: FormFilterType,
   *      operator: num/string Operators,
   *      value: string / number / {lte:number;gte:number}}
   * }
   */

  const onSubmitHandler = (values: FieldValues) => {
    const query = Object.values(values).reduce((acc, curr) => {
      const field = curr.field;
      const op = curr.operator.op;
      const value = curr.value;
      const isNested = !!field.nestedFieldName;

      let formattedValue =
        typeof value === "string"
          ? value
          : typeof value === "number"
          ? {
              [op]: value,
            }
          : {
              $gte: value.gte,
              $lte: value.lte,
            };

      acc[field.sectionName] = {
        ...acc[field.sectionName],
        [field.fieldName]: isNested
          ? {
              ...acc[field.sectionName]?.[field.fieldName],
              [field.nestedFieldName]: formattedValue,
            }
          : formattedValue,
      };

      return acc;
    }, {});

    setFilter(query);
  };

  return (
    <Box
      component={"form"}
      onSubmit={handleSubmit(onSubmitHandler)}
      display={"flex"}
      flexDirection={"column"}
      padding={theme.commonPadding}
      gap={theme.gap}
      border={theme.borderPrimary}
      borderRadius={theme.borderRadius}
    >
      {queryArray.map((queryValue, index) => {
        const watchedFieldValue = watch(`${queryValue.toString()}.field`) as
          | FormFilterType
          | undefined;

        const isGleOperator =
          watch(`${queryValue.toString()}.operator`)?.op === "$gle";

        const isValueOrOperatorDisabled = !watchedFieldValue;
        const valueType =
          watchedFieldValue?.type === FieldEntityTypeEnum.Number ||
          watchedFieldValue?.type === FieldEntityTypeEnum.NumberArray
            ? "number"
            : "text";

        const possibleValues = watchedFieldValue?.acceptabledValues;

        return (
          <Box
            key={queryValue}
            display={"flex"}
            flexDirection={"column"}
            gap={theme.gap}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              gap={theme.gap}
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
            >
              <Controller
                name={`${queryValue.toString()}.field`}
                control={control}
                defaultValue={""}
                rules={{
                  required: true,
                }}
                render={({ field: { ref, ...field }, fieldState }) => {
                  return (
                    <Autocomplete
                      {...field}
                      id={`field-${queryValue}-auto-complete`}
                      options={Object.values(options)}
                      disabled={!Object.keys(options).length}
                      autoComplete
                      includeInputInList
                      fullWidth
                      isOptionEqualToValue={(option, value) => {
                        return option.id === value?.id || value === "";
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id={`${queryValue.toString()}.field`}
                          label="Select Field"
                          inputRef={ref}
                          error={fieldState.invalid}
                          helperText={
                            fieldState.invalid ? "This field is required" : ""
                          }
                        />
                      )}
                      onChange={(_, value) => {
                        field.onChange(value);
                        setValue(`${queryValue.toString()}.operator`, "");
                        setValue(`${queryValue.toString()}.value`, "");
                      }}
                    />
                  );
                }}
              />

              <Controller
                name={`${queryValue.toString()}.operator`}
                control={control}
                defaultValue={""}
                rules={{
                  required: true,
                }}
                render={({ field: { ref, ...field }, fieldState }) => {
                  return (
                    <Autocomplete
                      {...field}
                      id={`operator-${queryValue}-auto-complete`}
                      options={
                        isValueOrOperatorDisabled
                          ? []
                          : valueType === "number"
                          ? numericalOperatorOptions
                          : stringOperatorOptions
                      }
                      disabled={isValueOrOperatorDisabled}
                      autoComplete
                      includeInputInList
                      fullWidth
                      isOptionEqualToValue={(option, value) => {
                        return option.op === value?.op || value === "";
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id={`${queryValue.toString()}.operator`}
                          label="Select operator"
                          inputRef={ref}
                          error={fieldState.invalid}
                          helperText={
                            fieldState.invalid ? "This field is required" : ""
                          }
                        />
                      )}
                      onChange={(_, value) => {
                        if (isGleOperator && value?.op !== "$gle") {
                          setValue(`${queryValue.toString()}.value`, "");
                        }

                        field.onChange(value);
                      }}
                    />
                  );
                }}
              />

              {possibleValues?.length ? (
                <Controller
                  name={`${queryValue.toString()}.value`}
                  control={control}
                  defaultValue={""}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { ref, ...field }, fieldState }) => {
                    return (
                      <Autocomplete
                        {...field}
                        id={`${queryValue.toString()}.value`}
                        options={possibleValues}
                        disabled={isValueOrOperatorDisabled}
                        autoComplete
                        includeInputInList
                        fullWidth
                        isOptionEqualToValue={(option, value) => {
                          return option === value || value === "";
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            id={`${queryValue.toString()}.value`}
                            label="Select value"
                            inputRef={ref}
                            error={fieldState.invalid}
                            helperText={
                              fieldState.invalid ? "This field is required" : ""
                            }
                          />
                        )}
                        onChange={(_, value) => field.onChange(value)}
                      />
                    );
                  }}
                />
              ) : isGleOperator ? (
                <Box
                  width={"100%"}
                  display={"flex"}
                  flexDirection={"column"}
                  gap={theme.gap}
                >
                  <Controller
                    name={`${queryValue.toString()}.value.gte`}
                    control={control}
                    defaultValue={""}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { ref, ...field }, fieldState }) => {
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          disabled={isValueOrOperatorDisabled}
                          id={`${queryValue.toString()}.value.gte`}
                          label={`Greater than or Equal Value`}
                          type={"number"}
                          placeholder={"Greater than or Equal Value"}
                          onChange={(event) => {
                            let value: string | number = parseFloat(
                              event.target.value
                            );

                            if (isNaN(value)) {
                              value = "";
                            }
                            field.onChange(value);
                          }}
                          inputRef={ref}
                          error={fieldState.invalid}
                          helperText={
                            fieldState.invalid ? "This field is required" : ""
                          }
                        />
                      );
                    }}
                  />

                  <Controller
                    name={`${queryValue.toString()}.value.lte`}
                    control={control}
                    defaultValue={""}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { ref, ...field }, fieldState }) => {
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          disabled={isValueOrOperatorDisabled}
                          id={`${queryValue.toString()}.value.lte`}
                          label={`Less than or Equal Value`}
                          type={"number"}
                          placeholder={"Less than or Equal Value"}
                          onChange={(event) => {
                            let value: string | number = parseFloat(
                              event.target.value
                            );

                            if (isNaN(value)) {
                              value = "";
                            }
                            field.onChange(value);
                          }}
                          inputRef={ref}
                          error={fieldState.invalid}
                          helperText={
                            fieldState.invalid ? "This field is required" : ""
                          }
                        />
                      );
                    }}
                  />
                </Box>
              ) : (
                <Controller
                  name={`${queryValue.toString()}.value`}
                  control={control}
                  defaultValue={""}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { ref, ...field }, fieldState }) => {
                    return (
                      <TextField
                        {...field}
                        fullWidth
                        disabled={isValueOrOperatorDisabled}
                        id={`${queryValue.toString()}.value`}
                        label={`Value`}
                        type={valueType}
                        onChange={(event) => {
                          let value: string | number = event.target.value;

                          if (valueType === "number") {
                            value = parseFloat(event.target.value);

                            if (isNaN(value)) {
                              value = "";
                            }
                          }
                          field.onChange(value);
                        }}
                        placeholder={"Add value"}
                        inputRef={ref}
                        error={fieldState.invalid}
                        helperText={
                          fieldState.invalid ? "This field is required" : ""
                        }
                      />
                    );
                  }}
                />
              )}
            </Box>

            <ButtonGroup fullWidth>
              {index === queryArray.length - 1 ? (
                <Button
                  onClick={() => {
                    setQueryArray((old) => [...old, old[old.length - 1] + 1]);
                  }}
                  startIcon={<AddCircle />}
                >
                  Add parameter
                </Button>
              ) : null}

              {queryArray.length > 1 ? (
                <Button
                  onClick={() => {
                    setQueryArray((old) => {
                      old.splice(index, 1);

                      unregister(queryValue.toString());

                      return [...old];
                    });
                  }}
                  startIcon={<RemoveCircle />}
                >
                  Remove parameter
                </Button>
              ) : null}
            </ButtonGroup>
          </Box>
        );
      })}
      <SaveButton type="submit">Search</SaveButton>
    </Box>
  );
};
